import { Person, SurveyResponse } from 'types';
import { ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import { TGroup } from 'views/Widgets/Filters/types';

export type CalibrationViewStatus = 'D' | 'L' | 'C';

export type CalibrationRecommendation = 'R' | 'K' | 'L';

export type CommentThreadType = CalibrationRecommendation | 'all';

export enum CalibrationCommentVisibilty {
  AUTHOR_AND_ADMINISTRATORS = 'A',
  AUTHOR_AND_REVIEWERS = 'R',
}

export type CalibrationComment = {
  id: number;
  survey_response: number;
  comment: string;
  recommendation?: CalibrationRecommendation;
  visibility: CalibrationCommentVisibilty;
  created_at: string;
  updated_at: string;
  author?: Person;
  parent_comment_id?: number | null;
};

export type ThreadedCalibrationComment = CalibrationComment & {
  thread_id: number;
  replies: ThreadedCalibrationComment[];
};

export type CalibrationSurveyResponse = SurveyResponse & {
  id?: number | string;
  conversation_rating?: number | string | null;
  calibration_flags?: unknown;
  person?: {
    id?: string | number | null;
    full_name?: string | null;
    email?: string | null;
    title?: string | null;
    avatar?: string | null;
    manager?: {
      id?: string | number | null;
      full_name?: string | null;
      email?: string | null;
      title?: string | null;
      avatar?: string | null;
    } | null;
  } | null;
  manager_review?: Record<string, string | number | null> | null;
  ona_metrics?: Record<string, number | null> | null;
  previous_manager_rating?: number | null;
  calibration_decision?: {
    proposed_rating?: string | null;
    comment?: string | null;
    status?: string | null;
  } | null;
  calibration_comments?: CalibrationComment[] | null;
};

export type CalibrationViewSummary = {
  id: number;
  name: string;
  status: CalibrationViewStatus;
  due_date: string | null;
  description?: string;
  calibratorCount: number;
  ownerPerson?: Person;
  administrators: Person[];
  reviewers: Person[];
  reviewees: Person[];
  revieweesCount: number;
  totalParticipants: number;
  completionPercent: number;
  flaggedCount: number;
  config?: {
    admin_target_audience?: ApiGroup;
    reviewees_filter?: ApiGroup;
    visible_columns?: string[];
    customer_data_columns?: Record<string, { available: boolean }>;
  };
};

export type CreateViewFormState = {
  name: string;
  description: string;
  due_date: string;
  status: CalibrationViewStatus;
  // TODO GroupByOption
  dimension: string;
  values: string[];
  ownerPerson: Person | null;
  administrators: Person[];
  reviewers: Person[];
  reviewees: Person[];
  advancedFilter: TGroup | null;
};

export type CalibrationDecisionState = {
  rating: string;
  comment: string;
};

export type CalibrationViewFilter = {
  dimension: string; // TODO GroupByOption
  values: string[];
};

export type CreateCalibrationCommentPayload = {
  comment: string;
  recommendation?: CalibrationRecommendation;
  visibility: CalibrationCommentVisibilty;
  parent_comment_id?: number;
  survey_response: number;
  calibration_view_id?: number;
};

export type UpdateCalibrationCommentPayload = Partial<
  Pick<CalibrationComment, 'comment' | 'visibility'>
> & {
  // null clears it; undefined would be dropped from the request
  recommendation?: CalibrationRecommendation | null;
};

export type BuildIconHeaderOptions = {
  iconNode?: React.ReactElement;
  description?: React.ReactNode;
};

export type ColumnHeaderPresentation = {
  content: React.ReactNode;
  popover: React.ReactNode | null;
};

export interface CalibrationViewPayload {
  organization_id: number;
  campaign_id: number;
  name: string;
  description?: string;
  status: CalibrationViewStatus;
  due_date: string;
  owner_person_id: number;
  administrator_ids?: number[];
  reviewer_ids: number[];
  config: {
    reviewees_filter?: ApiGroup;
    visible_columns?: string[];
    customer_data_columns?: Record<string, { available: boolean }>;
  };
}

export type CreateCommentHandler = (
  comment: CreateCalibrationCommentPayload,
  onSuccess: (comment: CalibrationComment) => void,
  onError: (error: any) => void
) => void;

export type EditCommentHandler = (
  comment: CalibrationComment,
  payload: UpdateCalibrationCommentPayload,
  onSuccess: (comment: CalibrationComment) => void,
  onError: (error: any) => void
) => void;
